import { ArrowUp, ArrowDown, Minus } from "lucide-react";
import { formatNumber, formatPercent } from "../../../../utils/format";

const DeltaCell = ({ current, previous }) => {
  if (previous === null || previous === undefined) {
    return <span className="text-gray-400 text-xs">—</span>;
  }
  const prev = Number(previous);
  const cur = Number(current);
  if (prev === 0 && cur === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
        <Minus size={12} /> 0
      </span>
    );
  }
  if (prev === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
        <ArrowUp size={12} /> Mới
      </span>
    );
  }
  const delta = ((cur - prev) / prev) * 100;
  if (Math.abs(delta) < 0.5) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
        <Minus size={12} /> 0%
      </span>
    );
  }
  const positive = delta > 0;
  const Icon = positive ? ArrowUp : ArrowDown;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${
        positive ? "text-emerald-600" : "text-rose-600"
      }`}
    >
      <Icon size={12} />
      {positive ? "+" : ""}
      {delta.toFixed(delta > 99 ? 0 : 1)}%
    </span>
  );
};

const PipelineTable = ({ stages = [], total = 0, comparison, loading }) => {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-10 rounded bg-gray-50 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (!stages || stages.length === 0) {
    return (
      <div className="text-sm text-gray-500 py-8 text-center">
        Chưa có dữ liệu
      </div>
    );
  }

  const sumCount = stages.reduce((s, r) => s + (Number(r.count) || 0), 0);
  const sumPrev = comparison
    ? stages.reduce((s, r) => s + (Number(r.previousCount) || 0), 0)
    : null;

  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">
            <th className="text-left font-semibold px-3 py-2">Giai đoạn</th>
            <th className="text-right font-semibold px-3 py-2">Contacts</th>
            <th className="text-right font-semibold px-3 py-2">% tổng</th>
            {comparison && (
              <th className="text-right font-semibold px-3 py-2">
                Δ kỳ trước
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {stages.map((s) => {
            const count = Number(s.count) || 0;
            return (
              <tr
                key={s.id ?? "unassigned"}
                className="border-t border-gray-100 hover:bg-gray-50/50"
              >
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: s.color || "#9ca3af" }}
                    />
                    <span className="text-gray-900 font-medium truncate">
                      {s.name}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {formatNumber(count)}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">
                  {formatPercent(count, sumCount || total || 1)}
                </td>
                {comparison && (
                  <td className="px-3 py-2.5 text-right">
                    <DeltaCell current={count} previous={s.previousCount} />
                  </td>
                )}
              </tr>
            );
          })}
          <tr className="border-t-2 border-gray-200 bg-gray-50/50 font-semibold">
            <td className="px-3 py-2.5 text-gray-900">Tổng</td>
            <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">
              {formatNumber(sumCount)}
            </td>
            <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">
              100%
            </td>
            {comparison && (
              <td className="px-3 py-2.5 text-right">
                <DeltaCell current={sumCount} previous={sumPrev} />
              </td>
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default PipelineTable;
